import React, { Suspense, lazy } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";

// ✅ Keep common components normal (important)
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import WhatsAppFloat from './components/layout/WhatsAppFloat.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import ScrollLink from './components/ScrollLink.jsx';

// ✅ Lazy loaded pages (code splitting)
const Home = lazy(() => import('./pages/Home.jsx'));
const About = lazy(() => import('./pages/About.jsx'));
const Services = lazy(() => import('./pages/Services.jsx'));
const Contact = lazy(() => import('./pages/Contact.jsx'));
const BookAppointment = lazy(() => import('./pages/BookAppointment.jsx'));
const Blog = lazy(() => import('./pages/Blog.jsx'));
const Gallery = lazy(() => import('./pages/Gallery.jsx'));
const AboutDrSridhar = lazy(() => import('./pages/AboutDrSridhar.jsx'));

const BlogPostDetail = lazy(() => import('./pages/BlogPostDetail'));
const FAQ = lazy(() => import('./pages/faq.jsx'));
const CaseStudy = lazy(() => import('./pages/CaseStudy.jsx'));
const DoctorTeam = lazy(() => import('./pages/DoctorTeam.jsx'));
const TakeATour = lazy(() => import('./pages/TakeATour.jsx'));

// ✅ Service pages
const SmileDesigning = lazy(() => import('./pages/Service/SmileDesigning.jsx'));
const SingleVisitDentistry = lazy(() => import('./pages/Service/SingleVisitDentistry.jsx'));
const DentalImplants = lazy(() => import('./pages/Service/DentalImplants.jsx'));
const PeriodontalTreatment = lazy(() => import('./pages/Service/PeriodontalTreatment.jsx'));
const LaserDentistry = lazy(() => import('./pages/Service/LaserDentistry.jsx'));
const RootCanal = lazy(() => import('./pages/Service/RootCanal.jsx'));
const OralSurgery = lazy(() => import('./pages/Service/OralSurgery.jsx'));
const PediatricDentistry = lazy(() => import('./pages/Service/PediatricDentistry.jsx'));
const CrownsBridgesDentures = lazy(() => import('./pages/Service/CrownsBridgesDentures.jsx'));
const AdvancedDentures = lazy(() => import('./pages/Service/AdvancedDentures.jsx'));
const RadiologyDepartment = lazy(() => import('./pages/Service/RadiologyDepartment.jsx'));
const RadiationSafety = lazy(() => import('./pages/Service/RadiationSafety.jsx'));
const PedodonticsDepartment = lazy(() => import('./pages/Service/PedodonticsDepartment.jsx'));
const Invisalign = lazy(() => import('./pages/Service/Invisalign.jsx'));

function App() {
  const location = useLocation();

  return (
    <>
      <Header />

      {/* Scroll helpers */}
      <ScrollToTop />
      <ScrollLink />

      {/* ✅ Suspense wrapper for lazy loading */}
      <Suspense fallback={<div className="text-center py-10">Loading...</div>}>
        <Routes location={location} key={location.pathname}>

          {/* Main routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/book-appointment" element={<BookAppointment />} />
          <Route path="/about-dr-sridhar" element={<AboutDrSridhar />} />

          {/* Services */}
          <Route path="/service/smile-Designing" element={<SmileDesigning />} />
          <Route path="/service/singleVisit-dentistry" element={<SingleVisitDentistry />} />
          <Route path="/service/dental-implants" element={<DentalImplants />} />
          <Route path="/service/periodontal-treatment" element={<PeriodontalTreatment />} />
          <Route path="/service/laser-dentistry" element={<LaserDentistry />} />
          <Route path="/service/root-canal" element={<RootCanal />} />
          <Route path="/service/oral-surgery" element={<OralSurgery />} />
          <Route path="/service/pediatric-dentistry" element={<PediatricDentistry />} />
          <Route path="/service/crownsBridges-dentures" element={<CrownsBridgesDentures />} />
          <Route path="/service/advanced-dentures" element={<AdvancedDentures />} />
          <Route path="/service/radiology-department" element={<RadiologyDepartment />} />
          <Route path="/service/radiation-safety" element={<RadiationSafety />} />
          <Route path="/service/pedodontics-department" element={<PedodonticsDepartment />} />
          <Route path="/service/invisalign" element={<Invisalign />} />

          {/* Other pages */}
          <Route path="/blog/:slug" element={<BlogPostDetail />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/case-study" element={<CaseStudy />} />
          <Route path="/doctor-team" element={<DoctorTeam />} />
          <Route path="/take-a-tour" element={<TakeATour />} />

          {/* 404 */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-blue-600 mb-4">404</h1>
                  <p className="text-2xl text-gray-700 mb-8">Page Not Found</p>
                  <Link
                    to="/"
                    className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition"
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            }
          />

        </Routes>
      </Suspense>

      <Footer />
      <WhatsAppFloat />
    </>
  );
}

export default App;